import React from "react";
import type { ContentCarouselTextImageSlide } from "@/types/content";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./ContentCarousel.module.css";

/** The slide's width: the carousel container, 1136px wide in the block grid. */
const SLIDE_SIZES = "(max-width: 1200px) 100vw, 1136px";

/**
 * One of the slide's two columns, 496px at most: the 1136px container less
 * 3rem padding each side and the 3rem gap, halved. One column from 768px down.
 */
const IMAGE_COLUMN_SIZES =
  "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 496px";

interface TextImageSlideProps {
  slide: ContentCarouselTextImageSlide;
  /** Whether to use lazy loading on the image */
  lazy?: boolean;
}

export const TextImageSlide: React.FC<TextImageSlideProps> = ({
  slide,
  lazy = true,
}) => {
  const isImageLeft = slide.imagePosition === "left";
  const hasBg = !!slide.backgroundImage?.url;

  return (
    <div
      className={`${styles.textImageSlide} ${isImageLeft ? styles.textImageSlideImageLeft : ""} ${hasBg ? styles.textImageSlideWithBg : ""}`}
    >
      {hasBg && (
        <>
          <img
            {...responsiveCmsImage(
              slide.backgroundImage!.url,
              SLIDE_SIZES,
              slide.backgroundImage!.width
            )}
            alt={slide.backgroundImage!.altText || ""}
            className={styles.textImageBg}
            loading={lazy ? "lazy" : undefined}
          />
          <div className={styles.textImageOverlay} aria-hidden="true" />
        </>
      )}

      <div className={styles.textImageTextPanel}>
        <h2 className={styles.textImageHeading}>{slide.heading}</h2>
        <div
          className={styles.richContent}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(slide.bodyText) }}
        />
      </div>

      <div className={styles.textImageImagePanel}>
        <img
          {...responsiveCmsImage(
            slide.image.url,
            IMAGE_COLUMN_SIZES,
            slide.image.width
          )}
          alt={slide.image.altText || ""}
          width={slide.image.width || undefined}
          height={slide.image.height || undefined}
          className={styles.textImageImage}
          loading={lazy ? "lazy" : undefined}
        />
      </div>
    </div>
  );
};
